import { useAuth } from "../lib/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import LanguageSwitcher from "./LanguageSwitcher";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { PenLine, LayoutDashboard, LogOut, ChevronDown, Shield, CreditCard, Settings, Calendar, BookOpen } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  const navLinkStyle = (path) => ({
    fontFamily: "'Outfit', sans-serif",
    color: isActive(path) ? "#00D4FF" : "#94A3B8",
    background: isActive(path) ? "rgba(0,212,255,0.08)" : "transparent",
  });

  return (
    <nav data-testid="navbar" className="sticky top-0 z-50 border-b"
      style={{ background: "rgba(11,22,34,0.8)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", borderColor: "rgba(30,58,95,0.4)" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
        <div className="flex items-center gap-4 sm:gap-8 flex-shrink-0">
          <button data-testid="logo-btn" onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 min-w-0">
            <img src="/favicon-anvil.png" alt="La Forja" className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg object-contain flex-shrink-0" />
            <span className="text-sm font-bold tracking-tight leading-none truncate" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#E2E8F0" }}>
              La Forja<span className="hidden sm:inline font-normal text-sm" style={{ color: "#94A3B8" }}> de Contenidos</span>
            </span>
          </button>
          <div className="hidden md:flex items-center gap-1">
            <button data-testid="nav-dashboard" onClick={() => navigate("/dashboard")}
              className="px-3 py-1.5 rounded-md text-sm transition-all duration-200" style={navLinkStyle("/dashboard")}>
              <LayoutDashboard className="w-4 h-4 inline mr-1.5 -mt-0.5" />{t("navbar.dashboard")}
            </button>
            <button data-testid="nav-pricing" onClick={() => navigate("/pricing")}
              className="px-3 py-1.5 rounded-md text-sm transition-all duration-200" style={navLinkStyle("/pricing")}>
              <CreditCard className="w-4 h-4 inline mr-1.5 -mt-0.5" />{t("navbar.pricing")}
            </button>
            <button data-testid="nav-armeria" onClick={() => navigate("/armeria")}
              className="px-3 py-1.5 rounded-md text-sm transition-all duration-200"
              style={{ ...navLinkStyle("/armeria"), color: window.location.pathname.startsWith("/armeria") ? "#c8922a" : "#94A3B8" }}>
              <span style={{ display: "inline-block", width: "16px", height: "16px", marginRight: "6px", marginTop: "-2px", verticalAlign: "middle", fontSize: "14px" }}>⚒</span>La Armería
            </button>
            <button data-testid="nav-calendar" onClick={() => navigate("/calendar")}
              className="px-3 py-1.5 rounded-md text-sm transition-all duration-200" style={navLinkStyle("/calendar")}>
              <Calendar className="w-4 h-4 inline mr-1.5 -mt-0.5" />{t("navbar.calendar")}
            </button>
            <button data-testid="nav-resources" onClick={() => navigate("/resources")}
              className="px-3 py-1.5 rounded-md text-sm transition-all duration-200" style={navLinkStyle("/resources")}>
              <BookOpen className="w-4 h-4 inline mr-1.5 -mt-0.5" />{t("navbar.resources")}
            </button>
            {user?.is_admin && (
              <button data-testid="nav-admin" onClick={() => navigate("/admin")}
                className="px-3 py-1.5 rounded-md text-sm transition-all duration-200" style={navLinkStyle("/admin")}>
                <Shield className="w-4 h-4 inline mr-1.5 -mt-0.5" />{t("navbar.admin")}
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitcher compact />
          <Button data-testid="new-article-btn" onClick={() => navigate("/new")}
            className="rounded-full px-3 sm:px-5 text-xs sm:text-sm font-medium transition-all duration-200"
            style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
            <PenLine className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />{t("navbar.new_article")}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-200"
                style={{ background: "rgba(0,212,255,0.05)", border: "1px solid rgba(30,58,95,0.4)" }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                  style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <span className="text-sm hidden sm:inline" style={{ color: "#E2E8F0", fontFamily: "'Outfit', sans-serif" }}>{user?.name}</span>
                <ChevronDown className="w-3.5 h-3.5" style={{ color: "#94A3B8" }} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <DropdownMenuItem disabled className="text-xs" style={{ color: "#94A3B8" }}>{user?.email}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/dashboard")} className="md:hidden" style={{ color: "#E2E8F0" }}>
                <LayoutDashboard className="w-3.5 h-3.5 mr-2" />{t("navbar.dashboard")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/pricing")} style={{ color: "#E2E8F0" }}>
                <CreditCard className="w-3.5 h-3.5 mr-2" />{t("navbar.pricing")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/calendar")} style={{ color: "#E2E8F0" }}>
                <Calendar className="w-3.5 h-3.5 mr-2" />{t("navbar.calendar")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/resources")} style={{ color: "#E2E8F0" }}>
                <BookOpen className="w-3.5 h-3.5 mr-2" />{t("navbar.resources")}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")} style={{ color: "#E2E8F0" }}>
                <Settings className="w-3.5 h-3.5 mr-2" />{t("common.settings")}
              </DropdownMenuItem>
              {user?.is_admin && (
                <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} style={{ color: "#E2E8F0" }}>
                  <Shield className="w-3.5 h-3.5 mr-2" />{t("navbar.admin")}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator style={{ background: "#1E3A5F" }} />
              <DropdownMenuItem data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} style={{ color: "#E2E8F0" }}>
                <LogOut className="w-3.5 h-3.5 mr-2" />{t("common.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
}
